// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import "bootstrap/dist/css/bootstrap.min.css";

import Navbar from "./Components/Common/Navbar.js";
import Footer from "./Components/Common/Footer.js";

import Login from './Components/Auth/Login';
import ProtectedRoute from './Pages/Utils/ProtectedRoute';

import ChiSiamo from "./Pages/ChiSiamo.js";
import Progetti from "./Pages/Progetti.js";
import FinanzaDiProgetto from "./Pages/FinanzaDiProgetto.js";
import LavoraConNoi from "./Pages/LavoraConNoi.js";
import Contatti from "./Pages/Contatti.js";
import VideoIntro from "./Pages/VideoIntro.js";

import Dashboard from "./Components/Dashboard/Dashboard.js";
import ProtectedProgetti from "./ProtectedPages/ProtectedProgetti.js";
import ProtectedFinanza from "./ProtectedPages/ProtectedFinanzaDiProgetto";
import ProtectedLogs from "./ProtectedPages/ProtectedAuditLogs.js";

import TestMenu from "./Pages/Test/TestMenu.js";
import TestChiSiamo from "./Pages/Test/TestChiSiamo.js";
import TestFinanza from "./Pages/Test/TestFinanza.js";
import { TestTransitionProvider, useTestTransition } from "./Pages/Test/TestTransitionContext.js";
import MenuSvgOverlay from "./Pages/Test/MenuSvgOverlay.js";

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Home() {
    const { openMenu } = useTestTransition();
    useEffect(() => {
        openMenu();
    }, [openMenu]);
    return null;
}

function App() {
    const { t } = useTranslation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
        setIsLoggedIn(loggedIn);
        setIsLoading(false);
    }, []);

    const handleLogin = () => {
        setIsLoggedIn(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('username');
        localStorage.removeItem('userId');
        setIsLoggedIn(false);
    };

    const footerPages = ["/progetti", "/finanza-di-progetto"];
    const showFooter = footerPages.includes(location.pathname);

    const protectedPages = ["/dashboard", "/protectedProgetti", "/protectedFinanza", "/protectedLogs"];
    const isCurrentPageProtected = protectedPages.includes(location.pathname);

    const isHomePage = location.pathname === "/" || location.pathname === "/home";
    const isChiSiamo = location.pathname === "/chi-siamo";
    const isTestMenu = location.pathname === "/test";

    // Blocca la scrollbar su html/body quando si è sulla home o chi-siamo (gestisce scroll internamente)
    // Per /test (menu) è bloccata; le sottopagine /test/* scrollano normalmente
    useEffect(() => {
        if (isHomePage || isChiSiamo || isTestMenu) {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        } else {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        }
        return () => {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        };
    }, [isHomePage, isChiSiamo, isTestMenu]);

    // Sfondo rosso su html/body quando si è sul test menu, per evitare barra bianca
    useEffect(() => {
        if (isTestMenu) {
            document.documentElement.style.background = 'linear-gradient(135deg, rgb(152,25,20), rgb(227,102,76))';
            document.body.style.background = 'linear-gradient(135deg, rgb(152,25,20), rgb(227,102,76))';
        } else {
            document.documentElement.style.background = '';
            document.body.style.background = '';
        }
        return () => {
            document.documentElement.style.background = '';
            document.body.style.background = '';
        };
    }, [isTestMenu]);

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <TestTransitionProvider>
        <div className={`App${isHomePage ? ' app-home' : ''}`}>
            {/* Navbar solo per le pagine pubbliche */}
            {!isCurrentPageProtected && <Navbar />}

            {/* Overlay SVG per test transitions */}
            <MenuSvgOverlay />

            <Routes location={location} key={location.pathname}>
                        {/* Pubbliche */}
                        <Route path="/" element={<VideoIntro />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/chi-siamo" element={<ChiSiamo />} />
                        <Route path="/progetti" element={<Progetti />} />
                        <Route path="/finanza-di-progetto" element={<FinanzaDiProgetto />} />
                        <Route path="/lavora-con-noi" element={<LavoraConNoi />} />
                        <Route path="/contatti" element={<Contatti />} />

                        {/* Test animazione transizione */}
                        <Route path="/test" element={<TestMenu />} />
                        <Route path="/test/chi-siamo" element={<TestChiSiamo />} />
                        <Route path="/test/finanza-di-progetto" element={<TestFinanza />} />

                        {/* Login */}
                        <Route
                            path="/login"
                            element={
                                isLoggedIn
                                    ? <Navigate to="/dashboard" replace />
                                    : <Login onLogin={handleLogin} />
                            }
                        />

                        {/* Protette */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute isLoggedIn={isLoggedIn}>
                                    <Dashboard onLogout={handleLogout} />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/protectedProgetti"
                            element={
                                <ProtectedRoute isLoggedIn={isLoggedIn}>
                                    <ProtectedProgetti />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/protectedFinanza"
                            element={
                                <ProtectedRoute isLoggedIn={isLoggedIn}>
                                    <ProtectedFinanza />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/protectedLogs"
                            element={
                                <ProtectedRoute isLoggedIn={isLoggedIn}>
                                    <ProtectedLogs />
                                </ProtectedRoute>
                            }
                        />

                        {/* Fallback */}
                        <Route
                            path="*"
                            element={
                                isLoggedIn
                                    ? <Navigate to="/dashboard" replace />
                                    : <Navigate to="/" replace />
                            }
                        />
                </Routes>

                {showFooter && !isCurrentPageProtected && <Footer />}

                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                />
            </div>
        </TestTransitionProvider>
    );
}

export default App;
